import Config from "@/common/config";

export type EtCoreTokenResponse = {
  access_token: string;
  expires_in: number;
  refresh_expires_in: number;
  refresh_token: string;
  token_type: "Bearer";
  id_token?: string;
  "not-before-policy"?: number;
  session_state?: string;
  scope?: string;
};

function getKeycloakTokenEndpoint(): string {
  const keycloakUrl = Config.keycloak.url.replace(/\/$/, "");
  const realm = encodeURIComponent(Config.keycloak.realm);

  return `${keycloakUrl}/realms/${realm}/protocol/openid-connect/token`;
}

function getKeycloakLogoutEndpoint(): string {
  const keycloakUrl = Config.keycloak.url.replace(/\/$/, "");
  const realm = encodeURIComponent(Config.keycloak.realm);

  return `${keycloakUrl}/realms/${realm}/protocol/openid-connect/logout`;
}

async function parseTokenResponse(
  response: Response,
): Promise<EtCoreTokenResponse> {
  if (!response.ok) {
    throw new Error(
      `ET Core Keycloak auth failed with status ${response.status}`,
    );
  }

  return (await response.json()) as Promise<EtCoreTokenResponse>;
}

async function loginWithPassword(
  username: string,
  password: string,
): Promise<EtCoreTokenResponse> {
  const body = new URLSearchParams();

  body.set("grant_type", "password");
  body.set("client_id", Config.keycloak.clientId);
  body.set("username", username);
  body.set("password", password);
  body.set("scope", "openid");

  const response = await fetch(getKeycloakTokenEndpoint(), {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body,
  });

  return parseTokenResponse(response);
}

async function refreshToken(
  refreshToken: string,
): Promise<EtCoreTokenResponse> {
  const body = new URLSearchParams();

  body.set("grant_type", "refresh_token");
  body.set("client_id", Config.keycloak.clientId);
  body.set("refresh_token", refreshToken);

  const response = await fetch(getKeycloakTokenEndpoint(), {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body,
  });

  return parseTokenResponse(response);
}

async function logout(refreshToken: string): Promise<void> {
  const body = new URLSearchParams();

  body.set("client_id", Config.keycloak.clientId);
  body.set("refresh_token", refreshToken);

  await fetch(getKeycloakLogoutEndpoint(), {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body,
  });
}

export const etCoreAuthAPI = {
  loginWithPassword,
  refreshToken,
  logout,
};
